import {
  ButtonGroup,
  Card,
  Col,
  Dropdown,
  DropdownButton,
  Form,
  Pagination,
  Row,
  Table,
} from "react-bootstrap";
import { useSelector } from "react-redux";
import { useWhitelabel } from "../../../contexts/WhitelabelContext";
import NotyfContext from "../../../contexts/NotyfContext";
import React, { useContext, useMemo, useState } from "react";
import useWindowSize from "../../../hooks/useWindowSize";
import { t } from "i18next";
import { exportReport } from "../../../redux/slices/reports";
import { DropdownSubmenu } from "react-bootstrap-submenu";
import FileSaver from "file-saver";
import { usePagination, useTable } from "react-table";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import useDeviceType from "../../../hooks/useDeviceType";

const IPAllocationTable = () => (
  <Table striped hover responsive>
    <thead>
      <tr>
        <th>Customer</th>
        <th>Allocation Type</th>
        <th>Start IP</th>
        <th>End IP</th>
        <th>IPs Count</th>
        <th>IP Type</th>
        <th>IP Configuration Name</th>
        <th>Operator</th>
        <th>Customer Subnet</th>
        <th>Domain Subnet</th>
      </tr>
    </thead>
    <tbody></tbody>
  </Table>
);

const InvoiceTable = ({ demoMode = false }) => {
  const invoiceReport = useSelector((state) => state.reports.invoiceReport);
  const { backgroundColor, fontColor } = useWhitelabel();
  const notyf = useContext(NotyfContext);
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";

  const dummyInvoiceReport = [
    {
      imsi: "123456789012345",
      description: "Monthly data plan - 1GB",
      quantity: 1,
      unitPrice: 5.0,
      tax: 1.2,
      costWithoutTax: 5.0,
      costWithTax: 6.2,
      currency: "EUR",
      offerName: "Basic Plan",
    },
    {
      imsi: "234567890123456",
      description: "SMS package - 100 messages",
      quantity: 1,
      unitPrice: 2.5,
      tax: 0.6,
      costWithoutTax: 2.5,
      costWithTax: 3.1,
      currency: "EUR",
      offerName: "SMS Lite",
    },
    {
      imsi: "345678901234567",
      description: "Voice minutes bundle - 500min",
      quantity: 2,
      unitPrice: 4.0,
      tax: 1.9,
      costWithoutTax: 8.0,
      costWithTax: 9.9,
      currency: "EUR",
      offerName: "Voice Max",
    },
    {
      imsi: "456789012345678",
      description: "Roaming add-on",
      quantity: 1,
      unitPrice: 7.0,
      tax: 1.68,
      costWithoutTax: 7.0,
      costWithTax: 8.68,
      currency: "EUR",
      offerName: "Roam Europe",
    },
  ];

  const data = useMemo(
    () => (demoMode ? dummyInvoiceReport : invoiceReport),
    [demoMode, invoiceReport]
  );

  const columns = useMemo(
    () => [
      {
        Header: t("reports.imsi"),
        accessor: "imsi",
      },
      {
        Header: t("reports.description"),
        accessor: "description",
      },
      {
        Header: t("reports.quantity"),
        accessor: "quantity",
      },
      {
        Header: t("reports.unitPrice"),
        accessor: "unitPrice", // Fixed accessor
      },
      {
        Header: t("reports.tax"),
        accessor: "tax",
      },
      {
        Header: t("reports.costWithoutTax"),
        accessor: "costWithoutTax",
      },
      {
        Header: t("reports.costWithTax"),
        accessor: "costWithTax",
      },
      {
        Header: t("reports.currency"),
        accessor: "currency",
      },
      {
        Header: t("reports.offerName"),
        accessor: "offerName",
      },
    ],
    []
  );

  const exportToCSV = async () => {
    const headers = columns.map((col) => col.Header);
    const rows = data.map((row) =>
      columns.map((col) => row[col.accessor] ?? "")
    );

    if (!demoMode) {
      // Backend export
      try {
        const response = await exportReport({
          headers,
          data: rows,
          format: "csv",
        });
        const blob = new Blob([response.data], { type: "text/csv" });
        FileSaver.saveAs(blob, "report.csv");
      } catch (error) {
        notyf.open({
          type: "danger",
          message: error,
          ripple: true,
          dismissible: false,
          duration: 5000,
          position: { x: "center", y: "top" },
        });
      }
    } else {
      // Frontend export
      const csvContent = [
        headers.join(","),
        ...rows.map((r) => r.join(",")),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      FileSaver.saveAs(blob, "report-demo.csv");
    }
  };

  const exportToPDF = async () => {
    const headers = columns.map((col) => col.Header);
    const rows = data.map((row) =>
      columns.map((col) => row[col.accessor] ?? "")
    );

    if (!demoMode) {
      // Backend export
      try {
        const response = await exportReport({
          headers,
          data: rows,
          format: "pdf",
        });
        const blob = new Blob([response.data], { type: "application/pdf" });
        FileSaver.saveAs(blob, "report.pdf");
      } catch (error) {
        notyf.open({
          type: "danger",
          message: error,
          ripple: true,
          dismissible: false,
          duration: 5000,
          position: { x: "center", y: "top" },
        });
      }
    } else {
      // Frontend export using jsPDF
      const doc = new jsPDF();
      doc.text("Demo Usage Report", 14, 10);

      autoTable(doc, {
        head: [headers],
        body: rows,
        styles: { fontSize: 8 },
        margin: { top: 20 },
      });
      doc.save("report-demo.pdf");
    }
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns: columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    usePagination
  );

  if (isDesktop) {
    return (
      <React.Fragment>
        <Row>
          <Col className="ms-auto" md="auto">
            <DropdownButton
              as={ButtonGroup}
              style={{
                "--dynamic-bg-color": backgroundColor,
                "--dynamic-font-color": fontColor,
                border: "none",
              }}
              title={t("reports.actions")}
            >
              <DropdownSubmenu title={t("reports.export")}>
                <Dropdown.Item eventKey="CSV" onClick={() => exportToCSV()}>
                  CSV
                </Dropdown.Item>
                <Dropdown.Item eventKey="PDF" onClick={() => exportToPDF()}>
                  PDF
                </Dropdown.Item>
              </DropdownSubmenu>
            </DropdownButton>
          </Col>
        </Row>
        <Table {...getTableProps()} striped hover responsive>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}>
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </Table>
        {invoiceReport.length > 0 && (
          <Row className="mt-4">
            <Col md="9">
              <span className="mx-2">
                {t("table.page")}{" "}
                <strong>
                  {pageIndex + 1} {t("table.of")} {pageOptions.length}
                </strong>
              </span>
              <span className="ms-3 me-2">{t("table.show")}:</span>
              <Form.Select
                className="d-inline-block w-auto"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                }}
              >
                {[5, 10, 20, 30, 40, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Form.Select>

              <span className="ms-3 me-2">{t("table.goToPage")}:</span>
              <Form.Control
                className="d-inline-block"
                type="number"
                defaultValue={pageIndex + 1}
                min={1}
                max={pageCount}
                onChange={(e) => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0;
                  gotoPage(page);
                }}
                style={{ width: "75px" }}
              />
            </Col>
            <Col md="3">
              <Pagination className="float-end">
                <Pagination.First
                  onClick={() => gotoPage(0)}
                  disabled={!canPreviousPage}
                />
                <Pagination.Prev
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                />
                <Pagination.Next
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                />
                <Pagination.Last
                  onClick={() => gotoPage(pageCount - 1)}
                  disabled={!canNextPage}
                />
              </Pagination>
            </Col>
          </Row>
        )}
      </React.Fragment>
    );
  }
};

const SimTable = () => (
  <Table striped hover>
    <thead>
      <tr>
        <th>ICCID</th>
        <th>IMSI</th>
        <th>MSISDN</th>
        <th>IP Address</th>
        <th>Platform</th>
        <th>Manager IP</th>
        <th>User IP</th>
        <th>User Email</th>
        <th>User Name</th>
        <th>Status</th>
        <th>Activation Date</th>
        <th>Offer</th>
      </tr>
    </thead>
    <tbody></tbody>
  </Table>
);

export { InvoiceTable, SimTable, IPAllocationTable };
